const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const pending = new Map();
const exactID=value=> typeof value==='string' && /^[1-9]\d{0,18}$/.test(value) && BigInt(value)<=9223372036854775807n || typeof value==='number' && Number.isSafeInteger(value) && value>0;
export const firstDepositRequestKey = (member,id) => `kg_first_deposit:v1:${member}:${id}`;
const money = value => typeof value === 'string' && /^(0|[1-9]\d*)\.\d{2}$/.test(value);
export function validFirstDepositReceipt(row,id,requestId,body) {
  if (!row || String(row.activityId) !== String(id) || row.requestId !== requestId || !uuidPattern.test(requestId) ||
      (typeof row.recordId !== 'string' || !exactID(row.recordId)) || !/^fdp1_[0-9a-f]{64}$/.test(row.planId) || !/^[a-f0-9]{64}$/.test(row.rulesVersion) ||
      !exactID(row.venueId) || typeof row.venueName !== 'string' || !row.venueName.trim() ||
      ![row.amount,row.bonusAmount,row.requiredTurnover].every(money) || /^0\.00$/.test(row.amount) || /^0\.00$/.test(row.bonusAmount) ||
      typeof row.transferNo !== 'string' || !row.transferNo || !['PROCESSING','SUCCESS','FAILED','MANUAL'].includes(row.transferStatus) ||
      row.destination !== ({SUCCESS:'VENUE',FAILED:'MAIN'}[row.transferStatus] || 'PENDING') ||
      typeof row.createdAt !== 'string' || !row.createdAt || typeof row.completedAt !== 'string') return false;
  return !body || row.planId === body.planId && row.rulesVersion === body.rulesVersion && String(row.venueId) === String(body.venueId) && row.amount === body.amount;
}

/** UUID survives success and unknown results: this activity is permanently once per member. Recovery never posts. */
export function createFirstDepositApplication({api,identity,token,storage,changed=()=>{},uuid=()=>crypto.randomUUID(),withLock}) {
  let generation=0,currentId='',view={visible:false,busy:false,requestId:'',receipt:null,error:''};
  const publish=patch=>{view={...view,...patch};changed({...view});};
  const scope=()=>({generation,member:String(identity()||''),token:token(),id:currentId});
  const live=s=>s.generation===generation&&s.member===String(identity()||'')&&s.token===token()&&s.id===currentId;
  const readKey=s=>firstDepositRequestKey(s.member,s.id);
  async function query(s,requestId,body) {
    try {
      const result=await api.firstDepositApplicationResult(s.id,requestId);
      if (!live(s)) return null;
      if (!validFirstDepositReceipt(result,s.id,requestId,body)) throw new Error('原申请凭证待核对，请保留编号');
      publish({visible:true,requestId,receipt:result,error:''});return result;
    } catch(error) {if(live(s)) publish({visible:true,requestId,receipt:null,error:error.message||'原申请尚待核对，请保留编号'});return null;}
  }
  async function open(id,serverRequestId='') {
    generation++;currentId=String(id||'');const s=scope();publish({visible:false,busy:false,requestId:'',receipt:null,error:''});
    if (!s.member||!s.token||!/^[1-9]\d*$/.test(s.id)) return;
    let requestId;
    try {requestId=storage.get(readKey(s))||serverRequestId;} catch {publish({visible:true,error:'无法读取原申请编号，请稍后重试'});return;}
    if (!requestId && api.firstDepositOriginal) {
      publish({busy:true});
      try {
        const original=await api.firstDepositOriginal(s.id);
        if(!live(s))return;
        if(!validFirstDepositReceipt(original,s.id,original?.requestId))throw new Error('原申请凭证待核对');
        publish({visible:true,busy:false,requestId:original.requestId,receipt:original,error:''});return;
      } catch(error) {
        if(!live(s))return;
        if(error.errorCode !== 'FIRST_DEPOSIT_RESULT_NOT_FOUND')publish({visible:true,busy:false,error:error.message||'原申请查询失败，请稍后重试'});
        else publish({busy:false});return;
      }
    }
    if (!requestId) return;
    if (!uuidPattern.test(requestId)) {publish({visible:true,error:'原申请编号待核对，请联系客服'});return;}
    publish({visible:true,busy:true,requestId});await query(s,requestId);if(live(s))publish({busy:false});
  }
  async function refresh() {const s=scope();if(view.busy||!s.token)return;if(!view.requestId)return open(s.id);publish({busy:true});await query(s,view.requestId);if(live(s))publish({busy:false});}
  async function submit(body) {
    const s=scope(),key=readKey(s);if(!exactID(s.member)||!s.token||!exactID(s.id)||view.busy||view.visible||pending.has(key))return null;
    const submittedBody=Object.freeze({planId:body?.planId,rulesVersion:body?.rulesVersion,venueId:body?.venueId,amount:body?.amount});
    if(!/^fdp1_[a-f0-9]{64}$/.test(submittedBody.planId||'')||!/^[a-f0-9]{64}$/.test(submittedBody.rulesVersion||'')||!exactID(submittedBody.venueId)||!money(submittedBody.amount)||submittedBody.amount==='0.00'){publish({error:'申请内容已变化，请重新确认'});return null;}
    const work=async granted=>{
      if(!granted||!live(s))return null;
      let requestId;
      try {
        const existing=storage.get(key);if(existing){publish({visible:true,requestId:existing,error:'已有原申请，请先核对结果'});await query(s,existing);return null;}
        requestId=uuid();if(!uuidPattern.test(requestId))throw new Error('请求编号生成失败');storage.set(key,requestId);if(storage.get(key)!==requestId)throw new Error('无法保留原申请编号');
      }catch(error){if(live(s))publish({error:error.message||'无法保留申请编号，尚未提交'});return null;}
      publish({visible:true,busy:true,requestId,error:'',receipt:null});
      try {
        const receipt=await api.firstDepositApplication(s.id,{...submittedBody,requestId});
        if(!live(s))return null;if(!validFirstDepositReceipt(receipt,s.id,requestId,submittedBody))throw new Error('原申请内容待核对');
        publish({receipt,error:''});return receipt;
      }catch(error){
        if(!live(s))return null;
        if(error.errorCode==='FIRST_DEPOSIT_APPLICATION_NOT_CREATED'){
          try{if(storage.get(key)===requestId)storage.remove(key);}catch{}
          publish({visible:false,requestId:'',receipt:null,error:error.message||'未创建申请，请重新填写'});return null;
        }
        publish({error:'申请结果尚待核对，请保留原编号，不要重复申请'});return await query(s,requestId,submittedBody);
      }finally{if(live(s))publish({busy:false});}
    };
    const operation=Promise.resolve().then(()=>withLock?withLock(key,work):work(true));pending.set(key,operation);
    try{return await operation;}finally{if(pending.get(key)===operation)pending.delete(key);}
  }
  return {open,refresh,submit,pause(){generation++;publish({visible:false,busy:false,requestId:'',receipt:null,error:''});},snapshot:()=>({...view})};
}

const centsText=value=>Number.isSafeInteger(value)&&value>=0?`${BigInt(value)/100n}.${String(BigInt(value)%100n).padStart(2,'0')}`:null;
const moneyCents=value=>{const [whole,fraction]=value.split('.');return BigInt(whole)*100n+BigInt(fraction);};
/** The confirmation uses a read-only server calculation bound to the exact displayed draft. */
export function validFirstDepositPreview(row,draft){
 if(!row||String(row.activityId)!==String(draft.activityId)||row.planId!==draft.tierId||row.rulesVersion!==draft.rulesVersion||String(row.venueId)!==String(draft.venueId)||row.venueName!==draft.venueName||row.amount!==draft.amount||
  ![row.amount,row.bonusAmount,row.totalAmount,row.requiredTurnover,row.applicationMaximum].every(money)||row.bonusAmount==='0.00'||
  row.bonusAmount!==centsText(draft.estimate?.bonusCents)||row.requiredTurnover!==centsText(draft.estimate?.turnoverCents)||
  moneyCents(row.totalAmount)!==moneyCents(row.amount)+moneyCents(row.bonusAmount)||moneyCents(row.applicationMaximum)<moneyCents(row.amount)||
  typeof row.verifiedAt!=='string'||!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(row.verifiedAt))return false;
 return true;
}
export async function readFirstDepositPreview(api,draft,{identity,token,isCurrent=()=>true}){
 const member=String(identity()||''),session=token();if(!member||!session)throw new Error('请先登录');
 if(!Number.isSafeInteger(Number(draft.venueId)))throw new Error('场馆编号不可用，请刷新活动');
 const result=await api.firstDepositPreview(draft.activityId,{planId:draft.tierId,rulesVersion:draft.rulesVersion,venueId:Number(draft.venueId),amount:draft.amount});
 if(member!==String(identity()||'')||session!==token()||!isCurrent())return null;
 if(!validFirstDepositPreview(result,draft))throw new Error('服务端预估与当前申请不一致，请刷新活动');
 return Object.freeze({...result});
}
